import { useEffect } from "react";
import { verifyShopifyAuth } from "../../../services/authService";
import { fetchProduct } from "../../../services/shopifyService";
import { useDispatch } from "react-redux";
import { setProfile } from "../../../redux/slice/profileSlice";
import { setSession } from "../../../redux/slice/authSlice";
import LoadingFallback from "../../../components/elements/LoadingFallback";
import { useNavigate } from "react-router-dom";
import { loadUserData, determineUserDestination } from "../../../util/navigationUtils";

const ShopifyCallback = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const handleShopifyCallback = async () => {
      try {
        // Extract URL parameters
        const params = new URLSearchParams(window.location.search);
        const customerId = params.get("customerId");
        const productId = params.get("productId") || params.get("planId");
        const variantId = params.get("variantId");

        if (!customerId) {
          navigate('/');
          return;
        }

        // Step 1: Authenticate with Shopify
        const { data: authData } = await verifyShopifyAuth({ customerId });
        
        if (!authData?.accessToken) {
          throw new Error('No access token received');
        }

        // Store token
        localStorage.setItem('auth_token', authData.accessToken);

        // Set initial session and profile
        dispatch(setProfile(authData));
        dispatch(setSession({ 
          user: { ...authData, access_token: authData.accessToken }, 
          status: "authorized", 
          error: null 
        }));

        // Step 2: Handle product/plan selection if present
        if (productId) {
          try {
            await handleProductSelection(productId, variantId);
          } catch (error) {
            console.error('Error fetching product:', error);
            // Continue even if product fetch fails
          }
        }

        // Step 3: Load user data (books, profile, images)
        const { profile, myBooks, activeBookId } = await loadUserData(
          authData._id, 
          dispatch,
          false // isComingFromNonUserRoute
        );

        // Step 4: Determine where to redirect based on user state
        const destination = determineUserDestination(profile, myBooks, activeBookId);

        // Step 5: Navigate to the determined destination
        console.log('Redirecting to:', destination);
        navigate(destination, { replace: true });

      } catch (error) {
        console.error('Authentication failed:', error);
        // Navigate to signin with error message
        navigate('/signin?error=shopify_auth_failed', { replace: true });
      }
    };

    handleShopifyCallback();
  }, [dispatch, navigate]);

  return <LoadingFallback />;
};

/**
 * Handles fetching and storing product/plan selection
 */
const handleProductSelection = async (productId, variantId) => {
  // Clear any existing plan
  localStorage.removeItem('selectedPlan');

  const product = await fetchProduct(`gid://shopify/Product/${productId}`);
  
  if (!product?.variants?.edges?.length) {
    throw new Error('No variants available for product');
  }

  // Find the selected variant
  let selectedVariant;

  if (variantId) {
    const variantEdge = product.variants.edges.find(
      edge => edge.node.id === variantId || 
              edge.node.id === `gid://shopify/ProductVariant/${variantId}`
    );
    selectedVariant = variantEdge?.node;

    if (!selectedVariant) {
      console.warn(`Variant ${variantId} not found, using first variant`);
      selectedVariant = product.variants.edges[0]?.node;
    }
  } else {
    selectedVariant = product.variants.edges[0]?.node;
  }

  if (!selectedVariant) {
    throw new Error('No variant could be selected');
  }

  // Build plan object
  const price = parseFloat(selectedVariant.price.amount);
  const planObj = {
    id: selectedVariant.id,
    productId: product.id,
    name: product.title,
    description: product.description,
    highLightTag: 0,
    highLightText: "Most Popular",
    planPricingData: { 
      price, 
      priceDescription: "Select Plan" 
    },
    features: [],
    shopifyProductId: product.id,
    shopifyVariantId: selectedVariant.id,
    image: product.images.edges[0]?.node?.url || null,
  };

  // Save to localStorage
  localStorage.setItem('selectedPlan', JSON.stringify(planObj));
  window.dispatchEvent(new Event("cartUpdated"));
  console.log('Plan saved:', planObj.name);

  return planObj;
};

export default ShopifyCallback;